import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarClock, Radio, CloudSun, ChevronDown, Bell, BellOff } from "lucide-react";
import useProgramReminders from "../hooks/useProgramReminders";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Palinsesto del giorno
 *
 * Legge /api/schedule (backend fa fetch RTF con If-Modified-Since).
 * Polling ogni 5 min — se la stazione aggiorna PlayList.rtf sul server,
 * l'app lo mostra automaticamente entro 5-10 min senza riavvio.
 */
export default function Palinsesto() {
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(true);
  const [lastSync, setLastSync] = useState("");
  const { reminders, toggle: toggleReminder } = useProgramReminders(data?.slots);

  useEffect(() => {
    let stop = false;
    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/schedule`, { params: { _: Date.now() }, timeout: 8000 });
        if (!stop) {
          setData(r.data);
          setLoaded(true);
          setLastSync(new Date().toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }));
        }
      } catch {
        if (!stop) setLoaded(true);
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 5 * 60 * 1000); // every 5 min
    const onVis = () => { if (!document.hidden) fetchOnce(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop = true;
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  if (!loaded || !data || !Array.isArray(data.slots) || data.slots.length === 0) return null;

  const current = data.current;
  const nxt = data.next;

  return (
    <section
      id="palinsesto"
      data-testid="palinsesto-section"
      className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10 border-b border-[#332f2b]"
    >
      <div className="flex items-end justify-between gap-4 mb-5 sm:mb-6">
        <div>
          <h2 className="font-anton uppercase text-2xl sm:text-3xl tracking-wide text-[#F5F2EB] flex items-center gap-3">
            <CalendarClock size={24} className="text-[#FF6B00]" />
            Palinsesto del giorno
          </h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#69635c] mt-2">
            {data.today} · ora {data.time_now}
            {lastSync && <span className="ml-2 text-[#4a443e]">· aggiornato alle {lastSync}</span>}
          </p>
        </div>
        <button
          type="button"
          data-testid="palinsesto-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="shrink-0 p-2 rounded-md border border-[#332f2b] text-[#A39A8F] hover:text-[#F5F2EB] transition"
          aria-label={open ? "Chiudi palinsesto" : "Apri palinsesto"}
        >
          <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* On-air NOW card */}
      {current && (
        <div
          data-testid="palinsesto-now"
          className="relative rounded-2xl border-2 border-[#FF6B00] bg-gradient-to-br from-[#FF6B00]/15 via-[#1a1816] to-[#1a1816] p-4 sm:p-5 mb-5 shadow-[0_0_28px_rgba(255,107,0,0.25)] overflow-hidden"
        >
          <div className="relative z-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] flex items-center gap-2">
              <span className="relative inline-flex w-1.5 h-1.5">
                <span className="absolute inline-flex w-full h-full rounded-full bg-[#FF6B00] opacity-70 animate-ping" />
                <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-[#FF6B00]" />
              </span>
              In onda ora · dalle {current.start} alle {current.end}
            </p>
            <h3 className="font-anton uppercase text-xl sm:text-2xl text-[#F5F2EB] mt-1.5 leading-tight">
              {current.program}
            </h3>
            {typeof current.minutes_in === "number" && current.minutes_total > 0 && (
              <div className="mt-3 h-1.5 w-full rounded-full bg-[#2a2622] overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-[#FF6B00] to-[#cf2020] transition-all duration-500"
                  style={{ width: `${Math.min(100, (current.minutes_in / current.minutes_total) * 100)}%` }}
                />
              </div>
            )}
          </div>
          <Radio size={120} className="pointer-events-none absolute -right-6 -bottom-6 text-[#FF6B00]/8" />
        </div>
      )}

      {/* Next up chip */}
      {nxt && (
        <p
          data-testid="palinsesto-next"
          className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#A39A8F] mb-5"
        >
          Prossimo · <span className="text-[#FF6B00]">{nxt.start}</span> — {nxt.program}
        </p>
      )}

      {open && (
        <ul data-testid="palinsesto-list" className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 sm:gap-2">
          {data.slots.map((s, i) => {
            const isCurrent = !s.is_meteo && current && current.start === s.start;
            const isReminded = !s.is_meteo && reminders.has(s.program);
            return (
              <li
                key={`${s.start}-${i}`}
                data-testid={`palinsesto-slot-${i}`}
                data-current={isCurrent ? "true" : undefined}
                className={`flex items-center gap-3 rounded-lg px-3 py-2.5 border transition ${
                  isCurrent
                    ? "border-[#FF6B00] bg-[#FF6B00]/10 shadow-[0_0_14px_rgba(255,107,0,0.2)]"
                    : s.is_meteo
                      ? "border-[#2a2622] bg-[#141210]/60"
                      : "border-[#332f2b] bg-[#1a1816] hover:border-[#FF6B00]/40"
                }`}
              >
                <span className={`shrink-0 font-mono text-[12px] font-semibold tabular-nums ${
                  isCurrent ? "text-[#FF6B00]" : s.is_meteo ? "text-[#5a544d]" : "text-[#F5F2EB]"
                }`}>
                  {s.start}
                </span>
                <span className={`shrink-0 w-7 h-7 rounded-md flex items-center justify-center ${
                  s.is_meteo
                    ? "bg-[#2a2622] text-[#69635c]"
                    : isCurrent
                      ? "bg-[#FF6B00]/25 text-[#FF6B00]"
                      : "bg-[#12100e] text-[#A39A8F] border border-[#332f2b]"
                }`}>
                  {s.is_meteo ? <CloudSun size={14} /> : <Radio size={14} />}
                </span>
                <span className={`font-oswald uppercase text-sm leading-tight truncate flex-1 ${
                  isCurrent ? "text-[#FF6B00]" : s.is_meteo ? "text-[#69635c] text-xs" : "text-[#F5F2EB]"
                }`}>
                  {s.program}
                </span>
                {!s.is_meteo && (
                  <button
                    type="button"
                    data-testid={`palinsesto-reminder-${i}`}
                    onClick={() => toggleReminder(s.program)}
                    aria-pressed={isReminded}
                    aria-label={isReminded ? `Rimuovi promemoria per ${s.program}` : `Avvisami quando inizia ${s.program}`}
                    title={isReminded ? "Promemoria attivo — tocca per rimuovere" : "Avvisami quando sta per iniziare"}
                    className={`shrink-0 w-9 h-9 rounded-md flex items-center justify-center border transition active:scale-90 ${
                      isReminded
                        ? "bg-[#FF6B00] border-[#FF6B00] text-[#12100e] shadow-[0_0_14px_rgba(255,107,0,0.4)]"
                        : "border-[#332f2b] text-[#A39A8F] hover:text-[#FF6B00] hover:border-[#FF6B00]/50"
                    }`}
                  >
                    {isReminded ? <Bell size={15} fill="currentColor" /> : <BellOff size={15} />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
